/**
 * Cart checkout: button loader + sync fee SKUs so checkout charges
 * the same amount as the cart estimated total.
 */
(() => {
  const CHECKOUT_BTNS = '#CartDrawer-Checkout, #checkout, .cart__checkout-button[name="checkout"]';
  const FEE_HANDLES = {
    design: ['mn-design-packaging-fee', 'design-packaging-fee', 'mn-design-fee'],
    color: ['mn-custom-color-fee', 'custom-color-fee', 'mn-color-fee'],
  };

  function getFeeConfig() {
    try {
      const el = document.getElementById('mn-cart-fee-config');
      return el?.textContent ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  }

  function getExpectedTotalCents() {
    const nodes = document.querySelectorAll('[data-mn-cart-display-total]');
    let max = 0;
    nodes.forEach((el) => {
      const n = Number(el.getAttribute('data-mn-cart-display-total'));
      if (Number.isFinite(n) && n > max) max = n;
    });
    return max;
  }

  function setCheckoutLoading(btn, loading) {
    if (!btn) return;
    const spinner = btn.querySelector('.loading__spinner');
    if (loading) {
      if (!btn.hasAttribute('data-mn-was-disabled')) {
        btn.setAttribute('data-mn-was-disabled', btn.hasAttribute('disabled') ? '1' : '0');
      }
      btn.classList.add('loading');
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('disabled', 'disabled');
      if (spinner) spinner.classList.remove('hidden');
      return;
    }

    btn.classList.remove('loading');
    btn.removeAttribute('aria-disabled');
    btn.removeAttribute('data-mn-checkout-busy');
    const wasDisabled = btn.getAttribute('data-mn-was-disabled') === '1';
    btn.removeAttribute('data-mn-was-disabled');
    if (wasDisabled) btn.setAttribute('disabled', 'disabled');
    else btn.removeAttribute('disabled');
    if (spinner) spinner.classList.add('hidden');
  }

  function resetAllCheckoutButtons() {
    document.querySelectorAll(CHECKOUT_BTNS).forEach((btn) => setCheckoutLoading(btn, false));
  }

  function moneyStringToCents(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value < 1000 ? Math.round(value * 100) : Math.round(value);
    }
    const raw = String(value).replace(/[^0-9.,-]/g, '').trim();
    if (!raw) return 0;
    const normalized =
      raw.includes(',') && raw.includes('.')
        ? raw.replace(/,/g, '')
        : raw.includes(',')
          ? raw.replace(',', '.')
          : raw;
    const n = Number(normalized);
    if (!Number.isFinite(n)) return 0;
    if (String(value).includes('.') || String(value).includes(',') || n < 1000) {
      return Math.round(n * 100);
    }
    return Math.round(n);
  }

  function resolveFeeVariantId(variants, priceCents, fallbackId) {
    const fee = Number(priceCents) || 0;
    const list = Array.isArray(variants) ? variants : [];
    if (fee > 0 && list.length) {
      const match =
        list.find((v) => Number(v.price) === fee) ||
        list.find((v) => Number(v.price) === Math.round(fee / 100)) ||
        list.find((v) => Number(v.price) === Math.round(fee * 100));
      if (match?.id) return Number(match.id);
    }
    const fb = Number(fallbackId) || 0;
    if (fb > 0) return fb;
    return Number(list[0]?.id) || 0;
  }

  async function fetchProductByHandles(handles) {
    const root = window.Shopify?.routes?.root || '/';
    for (const handle of handles) {
      if (!handle) continue;
      try {
        const product = await fetch(`${root}products/${handle}.js`).then((r) => (r.ok ? r.json() : null));
        if (product?.variants?.length) {
          return product.variants.map((v) => ({ id: v.id, price: Number(v.price) }));
        }
      } catch (e) {
        /* try next handle */
      }
    }
    return [];
  }

  async function ensureFeeVariants(config) {
    const next = { ...config };
    // Prefer configured variant ID (sample-style unlisted fallback) — no public product page needed.
    if (Number(next.designFeeVariantId) > 0 && !next.designFeeVariants?.length) {
      next.designFeeVariants = [
        { id: Number(next.designFeeVariantId), price: Number(next.designFeeCents) || 20000 },
      ];
    }
    if (!(Number(next.designFeeVariantId) > 0) || !next.designFeeVariants?.length) {
      const variants = await fetchProductByHandles(FEE_HANDLES.design);
      if (variants.length) {
        next.designFeeVariants = variants;
        const fee = Number(next.designFeeCents) || 20000;
        next.designFeeVariantId =
          resolveFeeVariantId(variants, fee, variants[0].id) || Number(next.designFeeVariantId) || 0;
      }
    }
    if (!next.colorFeeVariants?.length) {
      next.colorFeeVariants = await fetchProductByHandles(FEE_HANDLES.color);
    }
    return next;
  }

  async function fetchCart() {
    try {
      return await fetch(`${routes.cart_url}.js`).then((r) => r.json());
    } catch (e) {
      return null;
    }
  }

  async function addItems(items) {
    if (!items?.length) return null;
    const configFetch =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } };

    return fetch(`${routes.cart_add_url}`, {
      ...configFetch,
      headers: {
        ...(configFetch.headers || {}),
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    }).then((r) => r.json());
  }

  function isWallpaperMeasureLine(item) {
    const props = item.properties || {};
    if (!props || props._fee_type || props._hide_in_cart) return false;
    const width = props['Wall width (cm)'] || props['Wall Width (cm)'];
    const height = props['Wall height (cm)'] || props['Wall Height (cm)'];
    const hasMeasureInputs = width != null && width !== '' && height != null && height !== '';
    const hasDesignFeeProp = props._design_fee_cents != null && props._design_fee_cents !== '';
    return Boolean(hasMeasureInputs || hasDesignFeeProp);
  }

  function cartHasWallpaperMeasure(cart) {
    return (cart?.items || []).some(isWallpaperMeasureLine);
  }

  function lineNeedsDesignFee(item, hasDesignFee) {
    if (hasDesignFee) return false;
    return isWallpaperMeasureLine(item);
  }

  function lineNameFeeCents(item) {
    // Personalization fee sync only for wallpaper measure lines in this flow.
    if (!isWallpaperMeasureLine(item)) return 0;
    const props = item.properties || {};
    if (props._custom_name_fee_cents != null && props._custom_name_fee_cents !== '') {
      const n = Number(props._custom_name_fee_cents);
      return Number.isFinite(n) ? Math.round(n) : 0;
    }
    if (props['Custom name fee']) return moneyStringToCents(props['Custom name fee']);
    if (props['Custom name'] || props['Custom Name']) return 500;
    return 0;
  }

  function lineColorFeeCents(item) {
    if (!isWallpaperMeasureLine(item)) return 0;
    const props = item.properties || {};
    if (props._custom_color_fee_cents != null && props._custom_color_fee_cents !== '') {
      const n = Number(props._custom_color_fee_cents);
      return Number.isFinite(n) ? Math.round(n) : 0;
    }
    if (props['Custom color fee'] || props['Custom colors fee']) {
      return moneyStringToCents(props['Custom color fee'] || props['Custom colors fee']);
    }
    return 0;
  }

  function lineVariantId(item) {
    return Number(item?.variant_id || item?.id) || 0;
  }

  /** Merchandise variant to nest fee SKUs under in checkout. */
  function firstMerchandiseParentId(cart, predicate) {
    for (const item of cart?.items || []) {
      const props = item.properties || {};
      if (props._fee_type || props._hide_in_cart) continue;
      if (typeof predicate === 'function' && !predicate(item, props)) continue;
      const id = lineVariantId(item);
      if (id > 0) return id;
    }
    return 0;
  }

  function withParentId(feeItem, parentId) {
    if (!feeItem) return feeItem;
    const id = Number(parentId) || 0;
    if (id > 0) feeItem.parent_id = id;
    return feeItem;
  }

  /**
   * Wallpaper lines that need a custom-name fee SKU.
   * Returns { id, qty } per line — qty matches rolls (fee × quantity).
   */
  function wallpaperNameFeeNeeds(cart) {
    const needs = [];
    for (const item of cart?.items || []) {
      const props = item.properties || {};
      if (props._fee_type || props._hide_in_cart) continue;
      if (!(lineNameFeeCents(item) > 0)) continue;
      const id = Number(props._custom_name_fee_variant_id) || 0;
      if (id > 0) {
        needs.push({ id, qty: Math.max(1, Number(item.quantity) || 1) });
      }
    }
    return needs;
  }

  function wallpaperNameFeeTotalQty(cart) {
    return wallpaperNameFeeNeeds(cart).reduce((sum, n) => sum + n.qty, 0);
  }

  function findNameFeeLines(cart) {
    const neededIds = new Set(wallpaperNameFeeNeeds(cart).map((n) => n.id));
    return (cart?.items || []).filter((item) => {
      const props = item.properties || {};
      if (props._fee_type === 'custom_name') return true;
      const vid = lineVariantId(item);
      return vid > 0 && neededIds.has(vid);
    });
  }

  function cartHasNameFee(cart) {
    return findNameFeeLines(cart).length > 0;
  }

  function parseColorFeeVariantIds(props) {
    const raw = props?._custom_color_fee_variant_ids || props?._custom_color_fee_variant_id || '';
    return String(raw)
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => n > 0);
  }

  function resolveFeeVariantIdExact(variants, priceCents) {
    const fee = Number(priceCents) || 0;
    const list = Array.isArray(variants) ? variants : [];
    if (!(fee > 0) || !list.length) return 0;
    const match =
      list.find((v) => Number(v.price) === fee) ||
      list.find((v) => Number(v.price) === Math.round(fee / 100)) ||
      list.find((v) => Number(v.price) === Math.round(fee * 100));
    return match?.id ? Number(match.id) : 0;
  }

  function lineColorSelectionFeeCentsList(item) {
    const props = item?.properties || {};
    const data = props._custom_color_data;
    if (data) {
      const fees = String(data)
        .split('@@')
        .map((entry) => moneyStringToCents(String(entry).split('|')[3] || ''))
        .filter((n) => n > 0);
      if (fees.length) return fees;
    }
    const total = lineColorFeeCents(item);
    return total > 0 ? [total] : [];
  }

  /**
   * Needed color fee qty by variant id.
   * Prefers stored ids from ATC (one entry per selection); else resolves from data/prices.
   */
  function wallpaperColorFeeNeededById(cart, colorFeeVariants) {
    const needed = new Map();
    const variants = colorFeeVariants || [];
    for (const item of cart?.items || []) {
      const props = item.properties || {};
      if (props._fee_type || props._hide_in_cart) continue;
      if (!(lineColorFeeCents(item) > 0)) continue;
      const rolls = Math.max(1, Number(item.quantity) || 1);
      const ids = parseColorFeeVariantIds(props);
      if (ids.length) {
        ids.forEach((id) => needed.set(id, (needed.get(id) || 0) + rolls));
        continue;
      }
      lineColorSelectionFeeCentsList(item).forEach((feeCents) => {
        const id = resolveFeeVariantIdExact(variants, feeCents);
        if (id > 0) needed.set(id, (needed.get(id) || 0) + rolls);
      });
    }
    return needed;
  }

  function findColorFeeLines(cart) {
    return (cart?.items || []).filter((item) => item.properties?._fee_type === 'custom_color');
  }

  function merchandiseHasColorFee(cart) {
    return (cart?.items || []).some((item) => {
      const props = item.properties || {};
      if (props._fee_type || props._hide_in_cart) return false;
      return lineColorFeeCents(item) > 0;
    });
  }

  function buildMissingFeeItems(cart, config) {
    const toAdd = [];
    const hasDesignFee = cart.items.some((i) => i.properties?._fee_type === 'design_packaging');
    const hasNameFee = cartHasNameFee(cart);

    let designFeeVariantId = 0;
    let designParentId = 0;
    let nameFeeVariantId = 0;
    let nameFeeQty = 0;
    let nameParentId = 0;
    const colorFeeQtyById = new Map();
    let colorParentId = 0;

    const existingColorQty = new Map();
    findColorFeeLines(cart).forEach((line) => {
      const vid = lineVariantId(line);
      if (vid > 0) {
        existingColorQty.set(vid, (existingColorQty.get(vid) || 0) + (Number(line.quantity) || 0));
      }
    });
    const colorNeeded = wallpaperColorFeeNeededById(cart, config.colorFeeVariants);

    for (const item of cart.items) {
      const props = item.properties || {};
      if (props._fee_type || props._hide_in_cart) continue;
      const parentId = lineVariantId(item);
      const rolls = Math.max(1, Number(item.quantity) || 1);

      if (lineNeedsDesignFee(item, hasDesignFee)) {
        const fee = Number(props._design_fee_cents) || Number(config.designFeeCents) || 20000;
        const baked = Number(item.final_price) >= fee + 10000;
        if (!baked) {
          designFeeVariantId =
            Number(props._design_fee_variant_id) ||
            resolveFeeVariantId(config.designFeeVariants, fee, config.designFeeVariantId) ||
            designFeeVariantId;
          if (!designParentId && parentId > 0) designParentId = parentId;
        }
      }

      const nameCents = lineNameFeeCents(item);
      if (nameCents > 0 && !hasNameFee) {
        const id = Number(props._custom_name_fee_variant_id) || 0;
        if (id > 0) {
          nameFeeVariantId = id;
          nameFeeQty += rolls;
          if (!nameParentId && parentId > 0) nameParentId = parentId;
        }
      }

      if (lineColorFeeCents(item) > 0 && !colorParentId && parentId > 0) {
        colorParentId = parentId;
      }
    }

    colorNeeded.forEach((qty, id) => {
      const missing = qty - (existingColorQty.get(id) || 0);
      if (missing > 0) {
        colorFeeQtyById.set(id, (colorFeeQtyById.get(id) || 0) + missing);
      }
    });

    if (designFeeVariantId > 0) {
      toAdd.push(
        withParentId(
          {
            id: designFeeVariantId,
            quantity: 1,
            properties: { _fee_type: 'design_packaging', _hide_in_cart: '1' },
          },
          designParentId
        )
      );
    }
    if (nameFeeVariantId > 0 && nameFeeQty > 0) {
      toAdd.push(
        withParentId(
          {
            id: nameFeeVariantId,
            quantity: nameFeeQty,
            properties: { _fee_type: 'custom_name', _hide_in_cart: '1' },
          },
          nameParentId
        )
      );
    }
    colorFeeQtyById.forEach((qty, id) => {
      toAdd.push(
        withParentId(
          {
            id,
            quantity: qty,
            properties: { _fee_type: 'custom_color', _hide_in_cart: '1' },
          },
          colorParentId
        )
      );
    });

    return toAdd;
  }

  /**
   * If cart.total_price is still below the on-page estimated total after
   * normal fee sync, top up with design/name fee SKUs to close the gap.
   */
  function buildGapFillItems(cart, config, expectedCents) {
    const current = Number(cart?.total_price) || 0;
    let gap = expectedCents - current;
    if (!(gap > 0)) return [];

    const toAdd = [];
    const hasDesignFee = cart.items.some((i) => i.properties?._fee_type === 'design_packaging');
    const hasNameFee = cartHasNameFee(cart);

    const designId =
      Number(config.designFeeVariantId) ||
      resolveFeeVariantId(config.designFeeVariants, Number(config.designFeeCents) || 20000, 0);
    const designUnit =
      Number(config.designFeeVariants?.find((v) => Number(v.id) === designId)?.price) ||
      Number(config.designFeeCents) ||
      20000;

    const designParentId = firstMerchandiseParentId(cart, (item, props) =>
      Boolean(props._design_fee_cents || isWallpaperMeasureLine(item))
    );

    if (!hasDesignFee && designId > 0 && designUnit > 0 && gap >= designUnit * 0.5) {
      toAdd.push(
        withParentId(
          {
            id: designId,
            quantity: 1,
            properties: { _fee_type: 'design_packaging', _hide_in_cart: '1' },
          },
          designParentId
        )
      );
      gap -= designUnit;
    }

    let nameId = 0;
    let nameUnit = 0;
    let nameParentId = 0;
    for (const item of cart.items || []) {
      const props = item.properties || {};
      if (props._fee_type || props._hide_in_cart) continue;
      const cents = lineNameFeeCents(item);
      if (!(cents > 0)) continue;
      const id = Number(props._custom_name_fee_variant_id) || 0;
      if (id > 0) {
        nameId = id;
        nameUnit = cents;
        nameParentId = lineVariantId(item);
        break;
      }
    }

    const nameNeededQty = wallpaperNameFeeTotalQty(cart);
    if (nameNeededQty > 0 && !hasNameFee && nameId > 0 && gap > 0) {
      toAdd.push(
        withParentId(
          {
            id: nameId,
            quantity: nameNeededQty,
            properties: { _fee_type: 'custom_name', _hide_in_cart: '1' },
          },
          nameParentId
        )
      );
      gap -= nameUnit * nameNeededQty;
    }

    const colorNeeded = wallpaperColorFeeNeededById(cart, config.colorFeeVariants);
    const existingColorQty = new Map();
    findColorFeeLines(cart).forEach((line) => {
      const vid = lineVariantId(line);
      if (vid > 0) {
        existingColorQty.set(vid, (existingColorQty.get(vid) || 0) + (Number(line.quantity) || 0));
      }
    });
    if (colorNeeded.size > 0 && gap > 0) {
      const colorParentId = firstMerchandiseParentId(cart, (item) => lineColorFeeCents(item) > 0);
      colorNeeded.forEach((qty, id) => {
        const missing = qty - (existingColorQty.get(id) || 0);
        if (!(gap > 0) || !(missing > 0)) return;
        const unit =
          Number(config.colorFeeVariants?.find((v) => Number(v.id) === id)?.price) || 0;
        toAdd.push(
          withParentId(
            {
              id,
              quantity: missing,
              properties: { _fee_type: 'custom_color', _hide_in_cart: '1' },
            },
            colorParentId
          )
        );
        if (unit > 0) gap -= unit * missing;
      });
    }

    return toAdd;
  }

  async function applyCartUpdates(updates) {
    const keys = Object.keys(updates || {});
    if (!keys.length) return null;

    const configFetch =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } };

    await fetch(`${routes.cart_update_url}`, {
      ...configFetch,
      headers: {
        ...(configFetch.headers || {}),
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    }).then((r) => r.json());

    return fetchCart();
  }

  async function removeExtraDesignFees(cart) {
    const feeLines = (cart?.items || []).filter((i) => i.properties?._fee_type === 'design_packaging');
    if (feeLines.length <= 1) {
      // Still clamp a single line to qty 1 if it drifted.
      if (feeLines.length === 1 && Number(feeLines[0].quantity) !== 1) {
        const updated = await applyCartUpdates({ [feeLines[0].key]: 1 });
        return updated || cart;
      }
      return cart;
    }

    const updates = {};
    feeLines.forEach((line, index) => {
      updates[line.key] = index === 0 ? 1 : 0;
    });

    return (await applyCartUpdates(updates)) || cart;
  }

  /**
   * Keep custom-name fee SKU qty equal to total wallpaper rolls that need a name fee.
   * Consolidates duplicate fee lines onto the first one.
   */
  async function normalizeNameFeeQty(cart) {
    const neededQty = wallpaperNameFeeTotalQty(cart);
    const feeLines = findNameFeeLines(cart);
    if (!feeLines.length && neededQty === 0) return cart;

    const updates = {};
    if (neededQty <= 0) {
      feeLines.forEach((line) => {
        updates[line.key] = 0;
      });
    } else {
      feeLines.forEach((line, index) => {
        updates[line.key] = index === 0 ? neededQty : 0;
      });
    }

    const needsUpdate = Object.entries(updates).some(([key, qty]) => {
      const line = feeLines.find((l) => l.key === key);
      return !line || Number(line.quantity) !== Number(qty);
    });
    if (!needsUpdate && neededQty > 0) return cart;
    if (!Object.keys(updates).length) return cart;

    return (await applyCartUpdates(updates)) || cart;
  }

  async function normalizeColorFeeQty(cart, colorFeeVariants) {
    const needed = wallpaperColorFeeNeededById(cart, colorFeeVariants);
    const feeLines = findColorFeeLines(cart);
    if (!feeLines.length && needed.size === 0) return cart;

    if (needed.size === 0 && merchandiseHasColorFee(cart)) {
      return cart;
    }

    const updates = {};
    const remaining = new Map(needed);
    feeLines.forEach((line) => {
      const vid = lineVariantId(line);
      const want = remaining.has(vid) ? remaining.get(vid) : 0;
      if (want > 0) {
        updates[line.key] = want;
        remaining.set(vid, 0);
      } else {
        updates[line.key] = 0;
      }
    });

    const needsUpdate = Object.entries(updates).some(([key, qty]) => {
      const line = feeLines.find((l) => l.key === key);
      return !line || Number(line.quantity) !== Number(qty);
    });
    if (!needsUpdate) return cart;
    if (!Object.keys(updates).length) return cart;

    return (await applyCartUpdates(updates)) || cart;
  }

  async function syncWallpaperFees(cart) {
    if (!cart?.items?.length) {
      return { ok: true, cart, expectedCents: 0, gap: 0 };
    }

    if (!cartHasWallpaperMeasure(cart)) {
      return { ok: true, cart, expectedCents: 0, gap: 0, skipped: true };
    }

    const expectedCents = getExpectedTotalCents();
    let config = await ensureFeeVariants(getFeeConfig());

    let workingCart = (await removeExtraDesignFees(cart)) || cart;
    workingCart = (await normalizeNameFeeQty(workingCart)) || workingCart;
    workingCart = (await normalizeColorFeeQty(workingCart, config.colorFeeVariants)) || workingCart;

    const missing = buildMissingFeeItems(workingCart, config);
    if (missing.length) {
      const response = await addItems(missing);
      if (response?.status) {
        console.error('[mn-cart-checkout] Failed to add fee SKUs', response, missing);
      } else {
        workingCart = (await fetchCart()) || workingCart;
      }
    }

    if (expectedCents > 0 && Number(workingCart.total_price) < expectedCents) {
      const gapItems = buildGapFillItems(workingCart, config, expectedCents);
      if (gapItems.length) {
        const response = await addItems(gapItems);
        if (response?.status) {
          console.error('[mn-cart-checkout] Failed to fill checkout gap', response, gapItems);
        } else {
          workingCart = (await fetchCart()) || workingCart;
        }
      }
    }

    workingCart = (await removeExtraDesignFees(workingCart)) || workingCart;
    workingCart = (await normalizeNameFeeQty(workingCart)) || workingCart;
    workingCart = (await normalizeColorFeeQty(workingCart, config.colorFeeVariants)) || workingCart;

    const gap = Math.max(0, expectedCents - (Number(workingCart.total_price) || 0));
    const ok = !(expectedCents > 0 && gap > 1);

    if (!ok) {
      console.warn('[mn-cart-checkout] Checkout blocked: cart estimate not in Shopify cart total.', {
        expectedCents,
        cartTotal: workingCart.total_price,
        gap,
        needsColor: merchandiseHasColorFee(workingCart),
        colorConfigured: Boolean(config.colorFeeVariants?.length),
        colorNeeded: Object.fromEntries(
          wallpaperColorFeeNeededById(workingCart, config.colorFeeVariants) || []
        ),
        config,
      });
    }

    return { ok, cart: workingCart, expectedCents, gap, config };
  }

  function showCheckoutError(message) {
    const nodes = [
      document.getElementById('cart-errors'),
      document.getElementById('CartDrawer-CartErrors'),
    ].filter(Boolean);
    nodes.forEach((el) => {
      el.textContent = message;
      el.hidden = false;
    });
    if (!nodes.length) window.alert(message);
  }

  function consentBlocksCheckout(btn) {
    const formId = btn.getAttribute('form') || (btn.form && btn.form.id);
    const box = formId
      ? document.querySelector('.mn-cart-consent__input[form="' + formId + '"]')
      : document.querySelector('.mn-cart-consent__input');
    if (!box || box.checked) return false;
    box.focus();
    if (typeof box.reportValidity === 'function') box.reportValidity();
    return true;
  }

  async function onCheckoutClick(event) {
    const btn = event.target.closest(CHECKOUT_BTNS);
    if (!btn || btn.hasAttribute('data-mn-checkout-busy')) return;
    if (btn.disabled && !btn.classList.contains('loading')) return;

    if (consentBlocksCheckout(btn)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const form =
      btn.form ||
      document.getElementById(btn.getAttribute('form')) ||
      btn.closest('form');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();
    btn.setAttribute('data-mn-checkout-busy', 'true');
    setCheckoutLoading(btn, true);

    let syncResult = { ok: true };
    try {
      const cart = await fetchCart();
      syncResult = await syncWallpaperFees(cart);
    } catch (e) {
      console.error(e);
      syncResult = { ok: false };
    }

    if (!syncResult.ok) {
      setCheckoutLoading(btn, false);
      const needsColor = merchandiseHasColorFee(syncResult.cart);
      const message = needsColor
        ? 'Checkout total does not match your wallpaper cart. Set Fee variant on each odd/even Color Element Group metaobject (pointing at mn-custom-color-fee variants), clear the cart, re-add, then try again.'
        : 'Checkout total does not match your wallpaper cart. Ensure the Design & packaging fee product is available on Online Store at $200 (can stay out of collections), then try again.';
      showCheckoutError(message);
      return;
    }

    window.location.href = '/checkout';
  }

  // Back-forward cache keeps the old DOM; always clear checkout loaders on return.
  window.addEventListener('pageshow', resetAllCheckoutButtons);
  window.addEventListener('pagehide', resetAllCheckoutButtons);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resetAllCheckoutButtons();
  });

  document.addEventListener('click', onCheckoutClick, true);

  // Keep name/color fee qty aligned with wallpaper rolls when the cart UI is present.
  async function normalizeFeesQuietly() {
    try {
      const cart = await fetchCart();
      if (!cart?.items?.length || !cartHasWallpaperMeasure(cart)) return;
      const config = await ensureFeeVariants(getFeeConfig());
      let next = (await removeExtraDesignFees(cart)) || cart;
      next = (await normalizeNameFeeQty(next)) || next;
      await normalizeColorFeeQty(next, config.colorFeeVariants);
    } catch (e) {
      /* non-blocking */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeFeesQuietly, { once: true });
  } else {
    normalizeFeesQuietly();
  }
})();
